import React, { useState } from 'react';
import './Contact.css';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const FORM_API_URL = `${API_BASE_URL}/api/submit_form.php`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      projectType: '',
      message: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitError(false);

    try {
      const payload = {
        fullname: formData.name.trim(),
        emailId: formData.email.trim(),
        company: formData.company.trim(),
        phoneNo: formData.phone.trim(),
        project_type: formData.projectType.trim(),
        project_details: formData.message.trim()
      };

      const response = await fetch(FORM_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const rawResponse = await response.text();
      let result;

      try {
        result = JSON.parse(rawResponse);
      } catch (parseError) {
        const isHtmlResponse = rawResponse.trim().toLowerCase().startsWith('<!doctype')
          || rawResponse.trim().toLowerCase().startsWith('<html');

        throw new Error(
          isHtmlResponse
            ? 'PHP API not reached. Start a PHP server and set REACT_APP_FORM_API_URL to submit_form.php endpoint.'
            : 'API did not return valid JSON. Please check PHP endpoint URL/server configuration.'
        );
      }

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Unable to submit form right now.');
      }

      setSubmitMessage('Thank you! Your message has been sent successfully.');
      resetForm();
    } catch (error) {
      setSubmitError(true);
      setSubmitMessage(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: '📍',
      title: 'Address',
      details: ['Delaware, USA - 19901']
    },
    {
      icon: '\u260E\uFE0E',
      title: 'Contact',
      details: ['(302) 309-2552']
    },
    {
      icon: '✉️',
      title: 'Email',
      details: ['info@skylinesteeldesign.com']
    },
    {
      icon: '🕒',
      title: 'Business Hours',
      details: ['Monday - Friday: 07:00 AM - 05:30 PM EST']
    }
  ];

  return (
    <section className="contact" id="contact">
      <div className="container">
        <h2 className="section-title">Contact Us</h2>
        <p className="contact-subtitle">
          Ready to start your next steel project? Get in touch with our expert team today.
        </p>
        
        <div className="contact-content">
          <div className="contact-info">
            <h3>Get in Touch</h3>
            <p className="contact-description">
              Our team of steel experts is ready to help you with your next project. 
              Whether you need consultation, custom fabrication, or complete construction 
              solutions, we're here to deliver excellence.
            </p>
            
            <div className="contact-details">
              {contactInfo.map((info, index) => (
                <div key={index} className="contact-item">
                  <div className="contact-icon">{info.icon}</div>
                  <div className="contact-text">
                    <h4>{info.title}</h4>
                    {info.details.map((detail, detailIndex) => (
                      <p key={detailIndex}>{detail}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="contact-form-container">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="projectType">Project Type</label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                >
                  <option value="">Select Project Type</option>
                  <option value="commercial">Commercial Construction</option>
                  <option value="residential">Residential Construction</option>
                  <option value="industrial">Industrial Facility</option>
                  <option value="infrastructure">Infrastructure</option>
                  <option value="custom">Custom Fabrication</option>
                  <option value="consultation">Consultation</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Project Details *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Please describe your project requirements, timeline, and any specific needs..."
                  required
                ></textarea>
              </div>
              
              {submitMessage && (
                <p
                  style={{
                    margin: 0,
                    color: submitError ? '#ffd6d6' : '#d6ffe6',
                    fontWeight: 500
                  }}
                >
                  {submitMessage}
                </p>
              )}

              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;





